// =============================================================================
// Gamora — Badge critique réutilisable pour les hubs métier
// =============================================================================
// Lit /api/governance/gamora/hub/{hub} et affiche un badge 🔴 si critique.
// Au clic → modal avec liste des items.
// hub ∈ {conformite | commercial | operation | finance | communication}
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import { ShieldAlert } from "lucide-react";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { getTenantHeaders } from "../../lib/utils";
import { usePermissions } from "../../contexts/PermissionContext";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * @param {object} props
 * @param {"conformite"|"commercial"|"operation"|"finance"|"communication"} props.hub
 * @param {string} [props.label]  Libellé du hub (pour le titre modal)
 */
export default function GamoraHubBadge({ hub, label }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  // Gamora = Gouvernance → super_admin uniquement (Option 1, AUDIT-GAMORA-GATING).
  // Gate le fetch pour ne plus appeler /gamora/hub chez les admins non-super.
  const { isSuperAdmin } = usePermissions();
  const isSuper = isSuperAdmin();

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/governance/gamora/hub/${hub}`, {
        headers: getTenantHeaders(), credentials: "include",
      });
      if (res.ok) setData(await res.json());
    } catch { /* silent */ }
  }, [hub]);

  useEffect(() => {
    if (!isSuper) return;
    load();
    const int = setInterval(load, 5 * 60 * 1000); // refresh 5 min
    return () => clearInterval(int);
  }, [load, isSuper]);

  const n = data?.totals?.critique || 0;
  if (!isSuper || n === 0) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all hover:opacity-80"
        style={{ background: "rgba(220,38,38,0.12)", color: "#dc2626", border: "1px solid rgba(220,38,38,0.3)" }}
        data-testid={`gamora-badge-${hub}`}
        title="Anomalies critiques détectées par Gamora"
      >
        <ShieldAlert className="w-3.5 h-3.5" />
        {n} critique{n > 1 ? "s" : ""}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent style={{ background: "var(--oh-bg-surface)", maxWidth: 720 }}>
          <DialogHeader>
            <DialogTitle style={{ color: "var(--oh-text)" }} className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" style={{ color: "#dc2626" }} />
              Anomalies critiques — {label || hub}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {(data?.checks || []).map((c) => (
              <div key={c.id} className="rounded-lg p-3" style={{
                background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)",
              }}>
                <div className="flex items-center justify-between mb-2">
                  <strong style={{ color: "var(--oh-text)" }}>{c.label}</strong>
                  <Badge style={{ background: "#dc2626", color: "white" }}>
                    {c.count}
                  </Badge>
                </div>
                <div className="space-y-1">
                  {(c.items || []).slice(0, 20).map((it, i) => (
                    <div key={i} className="text-xs flex items-start gap-2"
                      style={{ color: "var(--oh-text-secondary)" }}>
                      <span style={{ color: "var(--oh-text-muted)" }}>•</span>
                      <span>
                        <strong>{it.ref || it.dossier_id || it.client_id || it.doc_id}</strong>
                        {it.evidence && (
                          <span style={{ marginLeft: 6, opacity: 0.75 }}>
                            {Object.entries(it.evidence).slice(0, 2).map(([k, v]) => (
                              <code key={k} style={{ marginRight: 6, fontSize: 11 }}>
                                {k}={typeof v === "object" ? JSON.stringify(v) : String(v).slice(0, 40)}
                              </code>
                            ))}
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                  {c.items?.length > 20 && (
                    <div className="text-xs italic" style={{ color: "var(--oh-text-muted)" }}>
                      … +{c.items.length - 20} autres (voir Gouvernance &gt; Gamora)
                    </div>
                  )}
                </div>
              </div>
            ))}
            {(!data?.checks || data.checks.length === 0) && (
              <p className="text-sm text-center py-4" style={{ color: "var(--oh-text-muted)" }}>
                Aucune anomalie critique pour ce hub.
              </p>
            )}
          </div>
          <div className="text-xs text-right pt-3 border-t mt-3"
            style={{ color: "var(--oh-text-muted)", borderColor: "var(--oh-border)" }}>
            Dernier run : {data?.run_at ? new Date(data.run_at).toLocaleString("fr-FR") : "—"}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
